import React from 'react';
import { createRoot } from 'react-dom/client';
import configureStore, { history } from './configureStore';
import Main from './main';
import { 
  parseInitialData, 
  transformInitialDataToPageState,
  transformInitialDataToConfigState,
  removeInitScript, 
  clearInitialData,
  hasInitialData 
} from './utils/initialDataParser';
import { parseNewFormat, mergeLocalVariablesIntoGlobal, attachStyle } from './utils/base';
import SessionHelper from './utils/sessionHelper';
import DeviceHelper from './utils/deviceHelper';

// Check for server-side rendered initial data
let initialState;
if (hasInitialData()) {
  try {
    const appData = parseInitialData();
    
    if (appData) {
      console.log('Initializing with server-side rendered data');
      
      // Transform the data to match the page state structure
      const transformedData = transformInitialDataToPageState(appData);
      
      if (transformedData) {
        // Parse the screen data using existing utilities
        const { page, settings, variableData } = appData.data.screen ? {
          page: appData.data.screen.sections,
          settings: transformedData.settings, // Use transformed settings which includes live_updates
          variableData: { ...appData.data?.variable_data, ...appData.data?.parameters },
        } : parseNewFormat(appData.data);
        
        initialState = {
          page: {
            screenId: appData.screen,
            loading: false,
            data: { page },
            settings: settings || {},
            variableData: mergeLocalVariablesIntoGlobal(variableData, settings?.variables),
            formValues: {},
            styleChunks: { refs: {}, indexes: {} },
            session_data: {
              type: 'initial',
              localShimmer: false,
              deviceId: DeviceHelper.getOrCreateDeviceId(),
              sessionId: SessionHelper.getOrCreateSessionId(),
            },
          },
        };
      }
      
      const configData = transformInitialDataToConfigState(appData);
      if (configData) {
        console.log('Initializing config from server-side data');
        initialState = {
          ...initialState,
          config: {
            ...configData,
            initializedFromInitScript: true,
          },
        };
        
        if (appData.data.config?.style) {
          attachStyle(appData.data.config.style);
        }
      }
      
      // Clean up after successful initialization
      removeInitScript();
      clearInitialData();
    }
  } catch (error) {
    console.error('Failed to initialize with initial data:', error);
    // Continue with normal initialization if parsing fails
  }
}

const store = configureStore(initialState);

const root = createRoot(document.getElementById('root')!);
root.render(<Main store={store} history={history} />);
